import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  TextField,
  Typography,
  Container,
  Grid,
  Checkbox,
  FormControlLabel,
  Popover,
  Box,
  Menu,
  MenuItem,
  Hidden
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { IconButton, InputAdornment } from '@mui/material';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import MenuIcon from '@material-ui/icons/Menu';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Helmet } from 'react-helmet';
import { AuthContext } from '../../context/Auth/AuthContext';
import ColorModeContext from '../../layout/themeContext';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4, 8),
    },
  },
  header: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logoImg: {
    height: '40px',
    width: 'auto',
    objectFit: 'contain',
  },
  navMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  navLink: {
    color: '#1c1e21',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.95rem',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'none',
      opacity: 0.8
    }
  },
  mobileMenuBtn: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  mainContent: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
  },
  gridContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftCol: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
      textAlign: 'center',
    },
  },
  rightCol: {
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(4),
      order: 2,
    },
  },
  heroTitle: {
    fontWeight: 800,
    fontSize: '3.5rem',
    lineHeight: 1.1,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  subText: {
    fontSize: '1.25rem',
    color: '#1c1e21',
    lineHeight: 1.5,
    marginBottom: theme.spacing(4),
    maxWidth: '500px',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
  },
  formContainer: {
    width: '100%',
    maxWidth: '400px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  inputRounded: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '30px',
      backgroundColor: '#f5f6f7',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: '1px solid #ccc',
      },
      '&.Mui-focused fieldset': {
        border: `1px solid ${theme.palette.primary.main}`,
      },
      '& input': {
        padding: '16.5px 14px',
      },
    },
  },
  // Container que agrupa o botão Entrar e o link Esqueceu a Senha
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    gap: theme.spacing(2), // Espaço entre o botão e o link
  },
  loginBtn: {
    borderRadius: '30px',
    padding: '10px 30px', // Padding mais horizontal
    fontSize: '1rem',
    fontWeight: 'bold',
    textTransform: 'none',
    boxShadow: 'none',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    width: 'auto', // Largura automática
    minWidth: '100px',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: 'none',
    },
  },
  forgotPass: {
    fontWeight: 500,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontSize: '0.95rem',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  rememberContainer: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  registerRow: {
    marginTop: theme.spacing(3),
    textAlign: 'center', // Centralizar o texto
    fontSize: '0.95rem',
    color: '#1c1e21',
  },
  mockupImg: {
    width: '100%',
    maxWidth: '600px',
    height: 'auto',
    borderRadius: '20px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '80%',
    }
  },
  footer: {
    marginTop: 'auto',
    paddingTop: theme.spacing(8), // Aumentado o espaço
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    fontSize: '0.8rem',
    color: '#767676',
    justifyContent: 'center',
  },
  resourcesContent: {
    padding: theme.spacing(4),
    maxWidth: '900px',
    borderRadius: '16px',
  },
  resourceCard: {
    padding: theme.spacing(2),
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      cursor: 'pointer'
    }
  },
  resourceTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  resourceDesc: {
    color: '#65676b',
    fontSize: '0.9rem',
    marginBottom: theme.spacing(2),
    minHeight: '40px',
  },
  arrowIcon: {
    color: theme.palette.primary.main,
    fontSize: '1.2rem',
  }
}));

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { handleLogin } = useContext(AuthContext);
  const { colorMode } = useContext(ColorModeContext);
  const [user, setUser] = useState({ email: '', password: '', remember: false });
  const [showPassword, setShowPassword] = useState(false);
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);

  const [resourcesAnchorEl, setResourcesAnchorEl] = useState(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://localhost:8090';

  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const url = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
        const response = await fetch(`${url}/settings/userCreation`);
        if (!response.ok) throw new Error('Status fetch failed');
        const data = await response.json();
        setUserCreationEnabled(data.userCreation === 'enabled');
      } catch (err) {
        console.error('Error userCreation:', err);
      }
    };
    fetchUserCreationStatus();
  }, [backendUrl]);

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  const logoSrc = colorMode.appLogoLight || colorMode.appLogoFavicon;

  const handleResourcesClick = (event) => {
    setResourcesAnchorEl(event.currentTarget);
  };

  const handleResourcesClose = () => {
    setResourcesAnchorEl(null);
  };

  const openResources = Boolean(resourcesAnchorEl);

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };
  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  const handleHelpClick = () => {
    const message = "Olá, gostaria de solicitar suporte técnico.";
    const phone = "5511939555609";
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Entrar - Multizap</title>
      </Helmet>

      <header className={classes.header}>
        <img src={logoSrc} alt='Logo' className={classes.logoImg} />

        <div className={classes.navMenu}>
          <div
            className={classes.navLink}
            onClick={handleResourcesClick}
            aria-owns={openResources ? 'resources-popover' : undefined}
            aria-haspopup="true"
          >
            Recursos <KeyboardArrowDownIcon fontSize="small" />
          </div>

          <div className={classes.navLink} onClick={handleHelpClick}>
            Central de Ajuda
          </div>
        </div>

        <Hidden mdUp>
          <IconButton
            className={classes.mobileMenuBtn}
            onClick={handleMobileMenuOpen}
          >
            <MenuIcon />
          </IconButton>
        </Hidden>

      </header>

      <Popover
        id="resources-popover"
        open={openResources}
        anchorEl={resourcesAnchorEl}
        onClose={handleResourcesClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          className: classes.resourcesContent,
          elevation: 4
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box className={classes.resourceCard}>
              <Typography variant="h5" className={classes.resourceTitle}>Multiatendimento</Typography>
              <Typography className={classes.resourceDesc}>
                Conecte toda sua equipe em um único número de WhatsApp e organize seu fluxo.
              </Typography>
              <ArrowForwardIcon className={classes.arrowIcon} />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box className={classes.resourceCard}>
              <Typography variant="h5" className={classes.resourceTitle}>Automação</Typography>
              <Typography className={classes.resourceDesc}>
                Crie fluxos de conversa automáticos (chatbots) para atender 24h por dia.
              </Typography>
              <ArrowForwardIcon className={classes.arrowIcon} />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box className={classes.resourceCard}>
              <Typography variant="h5" className={classes.resourceTitle}>Gestão</Typography>
              <Typography className={classes.resourceDesc}>
                Relatórios detalhados, etiquetas e gestão completa do seu time em tempo real.
              </Typography>
              <ArrowForwardIcon className={classes.arrowIcon} />
            </Box>
          </Grid>
        </Grid>
      </Popover>

      <Menu
        anchorEl={mobileMenuAnchorEl}
        keepMounted
        open={Boolean(mobileMenuAnchorEl)}
        onClose={handleMobileMenuClose}
      >
        <MenuItem onClick={() => { handleResourcesClick({ currentTarget: mobileMenuAnchorEl }); handleMobileMenuClose(); }}>Recursos</MenuItem>
        <MenuItem onClick={() => { handleHelpClick(); handleMobileMenuClose(); }}>Central de Ajuda</MenuItem>
      </Menu>

      <Container maxWidth='lg' className={classes.mainContent}>
        <Grid container className={classes.gridContainer} spacing={4}>
          <Grid item xs={12} md={6} className={classes.leftCol}>
            <Typography variant='h1' className={classes.heroTitle}>
              Um lugar para conversas significativas
            </Typography>

            <Typography variant='body1' className={classes.subText}>
              O Multizap ajuda você a conectar-se com seus clientes, gerenciar atendimentos e escalar seus resultados.
            </Typography>

            <form className={classes.formContainer} onSubmit={handleSubmit}>
              <TextField
                variant='outlined'
                placeholder='Email ou telefone'
                fullWidth
                value={user.email}
                onChange={(e) => setUser({ ...user, email: e.target.value })}
                className={classes.inputRounded}
                InputProps={{
                  startAdornment: null
                }}
              />

              <TextField
                variant='outlined'
                placeholder='Senha'
                fullWidth
                type={showPassword ? 'text' : 'password'}
                value={user.password}
                onChange={(e) => setUser({ ...user, password: e.target.value })}
                className={classes.inputRounded}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge='end'>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Botões alinhados */}
              <div className={classes.actionsRow}>
                <Button
                  type='submit'
                  variant='contained'
                  className={classes.loginBtn}
                >
                  Entrar
                </Button>

                <RouterLink to='/forgot-password' className={classes.forgotPass}>
                  Esqueceu a senha?
                </RouterLink>
              </div>

              {/* Checkbox Manter Conectado Abaixo */}
              <div className={classes.rememberContainer}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={user.remember}
                      onChange={(e) => setUser({ ...user, remember: e.target.checked })}
                      color='primary'
                    />
                  }
                  label='Manter conectado'
                />
              </div>

              {userCreationEnabled && (
                <div className={classes.registerRow}>
                  Ainda não tem uma conta?{' '}
                  <RouterLink to="/signup" style={{ textDecoration: 'none', fontWeight: 'bold' }}>
                    <span style={{ color: theme.palette.primary.main }}>Cadastre-se</span>
                  </RouterLink>
                </div>
              )}
            </form>
          </Grid>

          <Grid item xs={12} md={6} className={classes.rightCol}>
            <img
              src='https://minio3.sourcex.dev/typebot/public/workspaces/cmdkn2fsw0000pe17tv37l3vj/typebots/cmjh5wm5t0008t41aw8hxafcm/blocks/lqi7ikensf2lsxxbfxa8xgnf?v=1769034746048'
              alt='Conexão e Conversas'
              className={classes.mockupImg}
            />
          </Grid>

        </Grid>
      </Container>


      <footer className={classes.footer}>
        <span>© 2026 Multizap</span>
        <RouterLink to="/privacidade" style={{ color: 'inherit', textDecoration: 'none' }}>Privacidade</RouterLink>
        <RouterLink to="/termos" style={{ color: 'inherit', textDecoration: 'none' }}>Termos</RouterLink>
      </footer>
    </div>
  );
};

export default Login;
